import React, { useState, Component } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle, XCircle, Check } from 'lucide-react';
import { ProgressSteps } from './ProgressSteps';
import { PlanQuestionnaire, InvestorType, DealVolume, TeamSize } from './PlanQuestionnaire';
import { PlanRecommendation } from './PlanRecommendation';
import { recommendPlan, RecommendationResult } from '../../utils/planRecommendation';
import { pricingPlans } from '../../data/pricingPlans';
interface FormData {
  email: string;
  fullName: string;
  company: string;
  phone: string;
  dealVolume: DealVolume | '';
  investorType: InvestorType | '';
  teamSize: TeamSize | '';
  selectedPlan: 'Core' | 'Pro' | 'Scale' | '';
}
interface MultiStepFormProps {
  onSuccess: () => void;
}
/**
 * MultiStepForm component
 * Updated flow with smart plan recommendation
 * Step 1: Email only
 * Step 2-4: Questionnaire (deal volume, investor type, team size)
 * Step 5: Plan recommendation
 * Step 6: Account details
 * Step 7: Final confirmation
 */
export function MultiStepForm({
  onSuccess
}: MultiStepFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [questionnaireStep, setQuestionnaireStep] = useState(1);
  const [recommendation, setRecommendation] = useState<RecommendationResult | null>(null);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    fullName: '',
    company: '',
    phone: '',
    dealVolume: '',
    investorType: '',
    teamSize: '',
    selectedPlan: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const steps = [{
    number: 1,
    label: 'Email'
  }, {
    number: 2,
    label: 'Profile'
  }, {
    number: 3,
    label: 'Plan'
  }, {
    number: 4,
    label: 'Details'
  }];
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (step === 1) {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    if (step === 4) {
      if (!formData.fullName.trim()) {
        newErrors.fullName = 'Full name is required';
      }
      if (!formData.company.trim()) {
        newErrors.company = 'Company name is required';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handleQuestionnaireChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleQuestionnaireNext = () => {
    if (questionnaireStep < 3) {
      setQuestionnaireStep(questionnaireStep + 1);
    } else {
      // Generate recommendation
      const result = recommendPlan({
        dealVolume: formData.dealVolume as DealVolume,
        investorType: formData.investorType as InvestorType,
        teamSize: formData.teamSize as TeamSize
      });
      setRecommendation(result);
      setCurrentStep(3); // Move to recommendation step
    }
  };
  const handleQuestionnaireBack = () => {
    if (questionnaireStep > 1) {
      setQuestionnaireStep(questionnaireStep - 1);
    } else {
      setCurrentStep(1); // Back to email
    }
  };
  const handlePlanSelect = (planName: 'Core' | 'Pro' | 'Scale') => {
    setFormData(prev => ({
      ...prev,
      selectedPlan: planName
    }));
    setCurrentStep(4); // Move to details step
  };
  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 1) {
        setCurrentStep(2); // Move to questionnaire
      } else if (currentStep === 4) {
        handleSubmit();
      }
    }
  };
  const handleBack = () => {
    if (currentStep === 4) {
      setCurrentStep(3); // Back to recommendation
    } else if (currentStep === 3) {
      setQuestionnaireStep(3); // Back to last question
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(1); // Back to email
    }
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    // $97 onboarding fee checkout — credited toward first month's payment
    // Single Stripe link for the onboarding call fee
    const onboardingCheckoutUrl = 'https://buy.stripe.com/REPLACE_WITH_97_CHECKOUT_LINK';
    try {
      await fetch('/api/trial-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      const checkoutUrl = `${onboardingCheckoutUrl}?prefilled_email=${encodeURIComponent(formData.email)}&client_reference_id=${encodeURIComponent(formData.selectedPlan)}`;
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error during signup:', error);
      setIsSubmitting(false);
      const checkoutUrl = `${onboardingCheckoutUrl}?prefilled_email=${encodeURIComponent(formData.email)}&client_reference_id=${encodeURIComponent(formData.selectedPlan)}`;
      window.location.href = checkoutUrl;
    }
  };
  return <div data-id="element-2251">
      <ProgressSteps currentStep={currentStep} steps={steps} data-id="element-2252" />

      {/* Step 1: Email */}
      {currentStep === 1 && <div className="space-y-6 animate-fade-in" data-id="element-2253">
          <div className="text-center mb-6" data-id="element-2254">
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2255">
              Get started with AcquireFlow
            </h3>
            <p className="text-gray-400" data-id="element-2256">
              $97 onboarding fee credited toward your first month. Includes
              1-on-1 setup call.
            </p>
          </div>

          <div data-id="element-2257">
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2258">
              Work Email *
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`block w-full px-4 py-3 bg-[#1a1a1a] border ${errors.email ? 'border-red-500' : 'border-gray-700'} rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all`} placeholder="you@company.com" autoFocus data-id="element-2259" />
            {errors.email && <p className="mt-2 text-sm text-red-500 flex items-center gap-1" data-id="element-2260">
                <XCircle className="w-4 h-4" data-id="element-2261" />
                {errors.email}
              </p>}
          </div>

          <button onClick={handleNext} className="w-full px-6 py-4 bg-[#3ab795] text-white font-semibold rounded-lg hover:bg-[#2d9f7e] transition-all duration-300 flex items-center justify-center gap-2 group" data-id="element-2262">
            <span data-id="element-2263">Continue</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2264" />
          </button>
        </div>}

      {/* Step 2: Questionnaire */}
      {currentStep === 2 && <PlanQuestionnaire answers={{
      dealVolume: formData.dealVolume,
      investorType: formData.investorType,
      teamSize: formData.teamSize
    }} currentQuestion={questionnaireStep} onAnswerChange={handleQuestionnaireChange} onNext={handleQuestionnaireNext} onBack={handleQuestionnaireBack} data-id="element-2265" />}

      {/* Step 3: Plan Recommendation */}
      {currentStep === 3 && recommendation && <PlanRecommendation recommendation={recommendation} onSelectPlan={handlePlanSelect} onBack={() => {
      setQuestionnaireStep(3);
      setCurrentStep(2);
    }} data-id="element-2266" />}

      {/* Step 4: Account Details */}
      {currentStep === 4 && <div className="space-y-6 animate-fade-in" data-id="element-2267">
          <div className="text-center mb-6" data-id="element-2268">
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2269">
              Complete your profile
            </h3>
            <p className="text-gray-400" data-id="element-2270">
              You've selected the{' '}
              <span className="text-[#3ab795] font-semibold" data-id="element-2271">
                {formData.selectedPlan}
              </span>{' '}
              plan
            </p>
          </div>

          <div data-id="element-2272">
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2273">
              Full Name *
            </label>
            <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} className={`block w-full px-4 py-3 bg-[#1a1a1a] border ${errors.fullName ? 'border-red-500' : 'border-gray-700'} rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all`} placeholder="John Doe" autoFocus data-id="element-2274" />
            {errors.fullName && <p className="mt-2 text-sm text-red-500 flex items-center gap-1" data-id="element-2275">
                <XCircle className="w-4 h-4" data-id="element-2276" />
                {errors.fullName}
              </p>}
          </div>

          <div data-id="element-2277">
            <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2278">
              Company Name *
            </label>
            <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className={`block w-full px-4 py-3 bg-[#1a1a1a] border ${errors.company ? 'border-red-500' : 'border-gray-700'} rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all`} placeholder="Your Company" data-id="element-2279" />
            {errors.company && <p className="mt-2 text-sm text-red-500 flex items-center gap-1" data-id="element-2280">
                <XCircle className="w-4 h-4" data-id="element-2281" />
                {errors.company}
              </p>}
          </div>

          <div data-id="element-2282">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2283">
              Phone Number <span className="text-gray-500" data-id="element-2284">(Optional)</span>
            </label>
            <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="block w-full px-4 py-3 bg-[#1a1a1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all" placeholder="(555) 123-4567" data-id="element-2285" />
          </div>

          <div className="flex gap-3" data-id="element-2286">
            <button onClick={handleBack} disabled={isSubmitting} className="flex-1 px-6 py-4 bg-[#1a1a1a] border border-gray-700 text-white font-semibold rounded-lg hover:border-[#3ab795] transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2287">
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-2288" />
              <span data-id="element-2289">Back</span>
            </button>
            <button onClick={handleNext} disabled={isSubmitting} className="flex-1 px-6 py-4 bg-[#3ab795] text-white font-semibold rounded-lg hover:bg-[#2d9f7e] transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2290">
              {isSubmitting ? <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-2291">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2292"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-2293"></path>
                  </svg>
                  <span data-id="element-2294">Redirecting to checkout...</span>
                </> : <>
                  <span data-id="element-2295">Pay $97 & Book Onboarding</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2296" />
                </>}
            </button>
          </div>

          {/* Onboarding fee explanation */}
          <div className="mt-4 p-4 bg-[#3ab795]/5 border border-[#3ab795]/20 rounded-xl" data-id="element-2297">
            <p className="text-sm text-[#b8bcc8] text-center leading-relaxed" data-id="element-2298">
              <span className="text-[#3ab795] font-semibold" data-id="element-2299">
                $97 onboarding fee
              </span>{' '}
              includes a 1-on-1 setup call with our team and is credited toward
              your first month's payment.
            </p>
          </div>
        </div>}
    </div>;
}